import { FunctionComponent, ReactElement } from 'react';
import { Dayjs } from 'dayjs';
import { SsapFlowType } from '../../constants/ssap/flows/flowTypes';
import { IEX_STATE_CODES } from '@constants/configs/states';

export interface PersonName {
    firstName: string | null,
    middleName: string | null,
    lastName: string | null,
    suffix: string | null
}

type SsapPageName =
    | 'ABOUT_YOUR_HOUSEHOLD_PAGE'
    | 'ACTIVE_MILITARY_VETERAN_PAGE'
    | 'ADDITIONAL_INFORMATION_SUMMARY_PAGE'
    | 'ADDITIONAL_INFORMATION_PAGE'
    | 'APPLICATION_ASSISTANCE_PAGE'
    | 'APPLICANTS_PAGE'
    | 'AMERICAN_INDIAN_ALASKA_NATIVE_PAGE'
    | 'BEFORE_WE_BEGIN_PAGE'
    | 'CITIZENSHIP_IMMIGRATION_STATUS_PAGE'
    | 'DEDUCTION_SOURCES_PAGE'
    | 'DISABILITY_INFORMATION_PAGE'
    | 'EMPLOYER_COVERAGE_DETAIL_PAGE'
    | 'ETHNICITY_AND_RACE_PAGE'
    | 'EXPECTED_INCOME_PAGE'
    | 'FAMILY_AND_HOUSEHOLD_SUMMARY_PAGE'
    | 'FINAL_APPLICATION_REVIEW_PAGE'
    | 'FOSTER_CARE_INFORMATION_PAGE'
    | 'FULL_TIME_STUDENT_PAGE'
    | 'GET_READY_FOR_HOUSEHOLD_AND_FAMILY_PAGE'
    | 'GET_READY_FOR_INCOME_PAGE'
    | 'GET_READY_PAGE'
    | 'HELP_PAYING_F0R_COVERAGE_PAGE'
    | 'HOUSEHOLD_SUMMARY_PAGE'
    | 'INCOME_DISCREPANCY_PAGE'
    | 'INCOME_INFORMATION_SUMMARY_PAGE'
    | 'INCOME_SOURCES_PAGE'
    | 'MARITAL_STATUS'
    | 'MEDICAID_CHIP_DENIAL_INFORMATION_PAGE'
    | 'OTHER_ADDRESSES_PAGE'
    | 'OTHER_HEALTH_COVERAGE_PAGE'
    | 'PARENT_CARETAKER_INFORMATION_PAGE'
    | 'PERSONAL_INCOME_SUMMARY_PAGE'
    | 'PERSONAL_INFORMATION_PAGE'
    | 'PREGNANCY_INFORMATION_PAGE'
    | 'PRIMARY_CONTACT_INFORMATION_PAGE'
    | 'RECONCILIATION_IN_APTC_PAGE'
    | 'RELATIONSHIPS_PAGE'
    | 'REVIEW_AND_SIGN_PAGE'
    | 'SIGN_AND_SUBMIT_PAGE'
    | 'STATE_EMPLOYEE_HEALTH_BENEFIT_PAGE'
    | 'TAX_HOUSEHOLD_COMPOSITION_PAGE'
    | 'TRIBAL_SOURCE_INCOME_PAGE';

export type ReduxActionType =
    | 'FETCH_COMMUNICATION_PREFERENCES'
    | 'FETCH_COMMUNICATION_PREFERENCES_FAILURE'
    | 'FETCH_COMMUNICATION_PREFERENCES_START'
    | 'FETCH_COMMUNICATION_PREFERENCES_SUCCESS'
    | 'SET_COMMUNICATION_PREFERENCES'
    | 'CLEAR_DASHBOARD_STATE'
    | 'SET_ERROR';


export enum MemberRemovalReason {
    DEATH = 'DEATH',
    DIVORCE = 'DIVORCE',
    OTHER = 'OTHER',
    UNSPECIFIED = 'UNSPECIFIED'
}

export enum Attestations {
    AgreeToFileAttestation = 'AgreeToFileAttestation',
    AutoReEnrollmentAttestation = 'AutoReEnrollmentAttestation',
    ClaimNoticeAttestation = 'ClaimNoticeAttestation',
    CoverageEndAttestation = 'CoverageEndAttestation',
    IncarcerationAttestation = 'IncarcerationAttestation',
    IncomeDataUsageAttestation = 'IncomeDataUsageAttestation',
    MedicaidContactAttestation = 'MedicaidContactAttestation',
    MedicaidCooperationAttestation = 'MedicaidCooperationAttestation',
    MoneyPursueAttestation = 'MoneyPursueAttestation',
    NumberOfYearsAttestation = 'NumberOfYearsAttestation',
    OutsideParentAttestation = 'OutsideParentAttestation',
    PenaltyPerjuryAttestation = 'PenaltyPerjuryAttestation',
    ReadyReportChangesAttestation = 'ReadyReportChangesAttestation',
    SignatureAttestation = 'SignatureAttestation',
    TruthAttestation = 'TruthAttestation',
    VoterAndOrganDonorInformation = 'VoterAndOrganDonorInformation',
    VoterRegistrationAttestation = 'VoterRegistrationAttestation'
}

export enum DirtyFlag {
    ESI_MEC_DATA_DIRTY = 'esiMecDataDirty',
    NON_ESI_MEC_DATA_DIRTY = 'nonEsiMecDataDirty',
    SSAC_DATA_DIRTY = 'ssacDataDirty',
    VLP_DATA_DIRTY = 'vlpDataDirty'
}

type Gender =
    | 'male'
    | 'female'
    | null;

export enum GenderIdentification {
    MALE = 'MALE',
    FEMALE = 'FEMALE',
    TRANSGENDER_MALE = 'TRANSGENDER_MALE',
    TRANSGENDER_FEMALE = 'TRANSGENDER_FEMALE',
    NON_BINARY = 'NON_BINARY',
    UNCERTAIN = 'UNCERTAIN',
    OTHER = 'OTHER',
    UNSPECIFIED = 'UNSPECIFIED'
}

export enum SexualOrientation {
    LESBIAN_OR_GAY = 'LESBIAN_OR_GAY',
    STRAIGHT = 'STRAIGHT',
    BISEXUAL = 'BISEXUAL',
    UNCERTAIN = 'UNCERTAIN',
    OTHER = 'OTHER',
    UNSPECIFIED = 'UNSPECIFIED'
}

export enum ContactMethod {
    EMAIL = 'Email',
    MAIL = 'Mail',
    NONE = 'None'
}

export enum SsapSummaryType {
    ADDITIONAL_INFORMATION = 'ADDITIONAL_INFORMATION_HHM_${idx}',
    DEDUCTIONS = 'DEDUCTIONS',
    EMPLOYER_COVERAGE_DETAILS = 'EMPLOYER_COVERAGE_DETAILS',
    FAMILY_AND_HOUSEHOLD = 'FAMILY_AND_HOUSEHOLD_HHM_${idx}',
    HOUSEHOLD_COMMON = 'HOUSEHOLD_COMMON',
    HRA_DETAILS = 'HRA_DETAILS',
    INCOME_DISCREPANCY = 'INCOME_DISCREPANCY',
    INCOME_INFORMATION = 'INCOME_INFORMATION_HHM_${idx}',
    INCOMES = 'INCOMES',
    PRIMARY_CONTACT_PERSON = 'PRIMARY_CONTACT_PERSON',
    TRIBAL_INCOMES = 'TRIBAL_INCOMES',
    UNEMPLOYMENT_ATTESTATION = 'UNEMPLOYMENT_ATTESTATION'
}

export type RemovedMember = {
    demographic: {
        birthDate: string,
        deathDate: string | null,
        divorceDate: string | null,
        name: PersonName,
        otherDate: string | null
    },
    removalReasonType: MemberRemovalReason
}

export interface RemovedMembers {
    [key: string]: RemovedMember
}

export type Phone = string | null;

export enum Frequency {
    BIMONTHLY = 'BIMONTHLY',
    BIWEEKLY = 'BIWEEKLY',
    DAILY = 'DAILY',
    HOURLY = 'HOURLY',
    MONTHLY = 'MONTHLY',
    ONCE = 'ONCE',
    QUARTERLY = 'QUARTERLY',
    WEEKLY = 'WEEKLY',
    YEARLY = 'YEARLY',
    UNSPECIFIED = 'UNSPECIFIED'
}

export enum OtherProgramTypeForDirtyFlag {
    MEDICARE = 'MEDICARE',
    NONE = 'NONE',
    OTHER_COVERAGE = 'OTHER_COVERAGE',
    PEACE_CORPS = 'PEACE_CORPS'
}

export enum OtherProgramType {
    CHIP = 'CHIP',
    COBRA_COVERAGE = 'COBRA_COVERAGE',
    EMPLOYER_INSURANCE = 'EMPLOYER_INSURANCE',
    MARKETPLACE_COVERAGE = 'MARKETPLACE_COVERAGE',
    MEDICAID = 'MEDICAID',
    MEDICARE = 'MEDICARE',
    NONE = 'NONE',
    OTHER_COVERAGE = 'OTHER_COVERAGE',
    PEACE_CORPS = 'PEACE_CORPS',
    RETIRE_HEALTH_BENEFITS = 'RETIRE_HEALTH_BENEFITS',
    TRICARE = 'TRICARE',
    VETERANS_AFFAIRS_HCP = 'VETERANS_AFFAIRS_HCP'
}

export interface OtherStateOrFederalProgram {
    name: string,
    type: OtherProgramType,
    eligible: boolean
}

export enum IncomeType {
    AI_AN = 'AI_AN',
    ALIMONY = 'ALIMONY',
    CAPITAL_GAIN = 'CAPITAL_GAIN',
    DUMMY = 'DUMMY',
    FARMING_FISHING = 'FARMING_FISHING',
    INVESTMENT = 'INVESTMENT',
    JOB = 'JOB',
    OTHER = 'OTHER',
    PENSION = 'PENSION',
    RENTAL_ROYALTY = 'RENTAL_ROYALTY',
    RETIREMENT = 'RETIREMENT',
    SCHOLARSHIP = 'SCHOLARSHIP',
    SELF_EMPLOYMENT = 'SELF_EMPLOYMENT',
    SOCIAL_SECURITY_DISABILITY = 'SOCIAL_SECURITY_DISABILITY',
    UNEMPLOYMENT = 'UNEMPLOYMENT',
    UNSPECIFIED = 'UNSPECIFIED'
}

export enum IncomeSubtype {
    CANCELED_DEBT = 'CANCELED_DEBT',
    CASH_SUPPORT = 'CASH_SUPPORT',
    COURT_AWARD = 'COURT_AWARD',
    GAMBLING_PRIZE_AWARD = 'GAMBLING_PRIZE_AWARD',
    JURY_DUTY_PAY = 'JURY_DUTY_PAY',
    OTHER_INCOME = 'OTHER_INCOME',
    UNSPECIFIED = 'UNSPECIFIED'
}

export enum IncomeDifferenceReason {
    DECREASE_HOURS = 'DECREASE_HOURS',
    ENTERED_CORRECTLY = 'ENTERED_CORRECTLY',
    HOUSEHOLD_MEMBER_CHANGED = 'HOUSEHOLD_MEMBER_CHANGED',
    INCOME_LOSS = 'INCOME_LOSS',
    JOB_CHANGE = 'JOB_CHANGE',
    NOT_APPLICABLE = 'NOT_APPLICABLE',
    OTHER = 'OTHER',
    SELF_EMPLOYMENT_INCOME_FLUCTUATION = 'SELF_EMPLOYMENT_INCOME_FLUCTUATION',
    STOPPED_WORKING = 'STOPPED_WORKING',
    WAGE_CUT = 'WAGE_CUT'
}

export interface Income {
    amount: number | null,
    frequency: Frequency,
    sourceName: string | null,
    type: IncomeType,
    subType: IncomeSubtype,
    relatedExpense: number | null,
    tribalAmount: number | null,
    cyclesPerFrequency: number | null,
    incomeExpectedForWholeYear: boolean,
    incomeStartDate: string | null,
    incomeEndDate: string | null,
    incomeDifferenceReason: IncomeDifferenceReason,
    otherIncomeDifferenceReason: unknown,
    jobIncomeExplanationRequiredIndicator: boolean | null
}

export interface Enrollment {
    applicantGuid: string,
    hasActiveHealthEnrollment: boolean
}

export type Address = {
    addressId: number,
    city: string | null,
    county: string | null,
    countyCode: string | null,
    deletable: boolean,
    postalCode: string | null,
    primaryAddressCountyFipsCode: string | null,
    streetAddress1: string | null,
    streetAddress2: string | null,
    state: string | null // replace string with exact values later
};

export interface EmployerSponsoredCoverage {
    address: Address,
    applicantGuid: string,
    companyName: string | null,
    ein: string | null,
    email: string | null,
    employerOfferAffordable: boolean,
    name: PersonName,
    phone: Phone
}

export interface HraDetails {
    personId: string | null,
    applicantGuid: string | null,
    enrolledInIchraIndicator: boolean | null,
    employerPremiumPercentage: number | null,
    employerOfferAffordable: boolean | null,
    hraType: string | null,
    primaryInsuredMemberIdentifier: string | null,
    startDate: string | null,
    endDate: string | null,
    employeeSelfOnlyOfferAmount: 0,
    employeeSelfOnlyOfferFrequencyType: Frequency.MONTHLY,
    primaryInsuredMemberNotInTaxHhIndicator: boolean | null,
    employerName: string | null,
    employerIdentificationNumber: string | null,
    streetName1: string | null,
    streetName2: string | null,
    cityName: string | null,
    stateCode: IEX_STATE_CODES | null,
    zipCode: string | null,
    plus4Code: string | null,
    countryCode: string | null,
    countyName: string | null,
    countyFipsCode: string | null,
    employerPhoneNumber: string | null,
    name: {
        firstName: string | null,
        middleName: string | null,
        lastName: string | null,
        suffix: string | null,
    },
    email: string | null,
    phoneNumber: string | null,
}

export interface IsState {
    CA: boolean,
    ID: boolean,
    IL: boolean,
    MN: boolean,
    NM: boolean,
    NV: boolean,
    PA: boolean,
    NJ: boolean,
    GA: boolean,
    VA: boolean,
    GI: boolean
}

export interface DropdownOption {
    value: IncomeDifferenceReason, // Add here possible values
    text: string
}

export type DropdownOptions = Array<DropdownOption>

export interface SocialSecurityCard {
    firstNameOnSSNCard: string | null,
    lastNameOnSSNCard: string | null,
    middleNameOnSSNCard: string | null,
    nameSameOnSSNCardIndicator: boolean | null,
    noSsnIssuedAttestation: boolean | null,
    socialSecurityCardHolderIndicator: boolean | null,
    socialSecurityNumber: string | null,
    suffixOnSSNCard: string | null
}

interface SpecialCircumstances {
    deceased: boolean | null,
    deceasedDate: string | null,
    dueDateOfPregnancy: string | null,
    numberBabiesExpectedInPregnancy: number,
    pregnantIndicator: boolean | null
}

type INLINE_ELIGIBILITY =
    | 'YES'
    | 'NO' // It might be that 'NO' does not exist
    | 'NA'

interface TaxHouseholdComposition { // TODO: convert THC_TYPES to enum and apply here
    claimerIds : Array<number>,
    independentTaxFiler: boolean,
    taxFilingStatus: string,
    taxRelationship: string
}

type Language =
    | 'English'
    | 'Spanish'
    | null

type ImmigrationInfoMaskedProps =
    | 'alienNumber'
    | 'foreignPassportOrDocumentNumber'
    | 'i94Number'
    | 'sevisId'
    | 'visaNumber'

export interface CachedData {
    immigrationInfo: {
        [ key in ImmigrationInfoMaskedProps ]: string | null
    },
    previousImmigrationInfo: {
        [ key in ImmigrationInfoMaskedProps ]: string | null
    },
    previousSSNs: {
        [ key: string ]: string | null
    },
    ssns: {
        [ key: string ]: string | null
    },
}


interface ContactPreferences {
    emailAddress: string | null,
    emailMe: boolean | null,
    paperless1095: boolean | null,
    preferredContactMethod: ContactMethod,
    preferredSpokenLanguage: Language,
    preferredWrittenLanguage: Language,
    textMe: boolean | null
}

export interface DirtyMedicaidVerification {
    esiMecDataDirty: boolean | null,
    nonEsiMecDataDirty: boolean | null,
    ssacDataDirty: boolean | null,
    vlpDataDirty: boolean | null
}

export interface HouseholdMember {
    applicantGuid: string,
    applyingForCoverageIndicator: boolean | null,
    citizenshipImmigrationStatus: {
        citizenshipStatusIndicator: boolean | null,
        lawfulPresenceIndicator: boolean | null
    },
    dateOfBirth: string | null,
    employerSponsoredCoverage: EmployerSponsoredCoverage[],
    gender: Gender,
    hasESI: boolean,
    incarcerationStatus: {
        incarcerationStatusIndicator: boolean | null,
        incarcerationPendingDispositionIndicator: boolean | null
    },
    incomes: Array<Income>,
    inlineEligibility: {
        preliminaryChipStatus: INLINE_ELIGIBILITY,
        preliminaryMedicaidStatus: INLINE_ELIGIBILITY
    },
    healthCoverage: {
        absentParent: boolean | null,
        offeredIchraIndicator: boolean | null
    },
    healthReimbursementArrangement: Array<HraDetails>,
    householdContact: {
        outOfStateAttestation: boolean | null,
        contactPreferences: ContactPreferences
    },
    householdId: number,
    medicaidVerification: DirtyMedicaidVerification,
    name: PersonName,
    parentCaretaker: boolean | null,
    personId: number,
    socialSecurityCard: SocialSecurityCard,
    specialCircumstances: SpecialCircumstances,
    taxHouseholdComposition: TaxHouseholdComposition
}

export type HouseholdMembers = Array<HouseholdMember>;

export type Page = number;

export type DestinationPage = 'increase' | 'decrease' | Page;

export interface InstructionalTextPropTypes {
    anchor?: string,
    anchorId: string,
    childrenReplacements?: {},
    Component: ReactElement,
    disabled?: boolean,
    expandClassName?: string,
    fadeEffectDisabled?: boolean,
    instructionId?: string;
    onlyInstructions?: boolean,
    required?: boolean,
    replacements?: {},
    reactPortalAnchor?: {
        current: HTMLElement
    },
    skipParsing?: boolean,
    styleType?: 'MODAL' | 'PARAGRAPH'
}

export interface CommunicationPreferences {
    emailAddress: string | null,
    phoneNumber: string | null,
    preferredSpokenLanguage: string | null,
    preferredWrittenLanguage: string | null,
    mailingAddress: Address,
    textMe: boolean | null,
    paperless1095: boolean | null,
    emailMe: boolean | null,
    paperlessNotices: boolean | null,
    userRecordExists: boolean | null,
    middleName: string | null,
    nameSuffix: string | null,
    firstName: string | null,
    lastName: string | null,
    ssn: string | null,
    mailinglocationId: string | null,
    primaryLocationId: string | null,
    birthDate: string | null,
    showNoticeOptOut: boolean | null,
    noticeOptOut: boolean | null,
    mailingAddressUpdateAllowed: boolean | null
}

export interface BloodRelationship {
    individualPersonId: string,
    relatedPersonId: string,
    relation: string,
    personGuid: string,
    relatedPersonGuid: string
}

export type BloodRelationships = Array<BloodRelationship>;

interface Relation {
    text: string,
    type: string,
    code: string,
    mirrorType: string
}

interface GenericRelation extends Omit<Relation, 'mirrorType'>{}

export interface FamilyStatus {
    dependents: Array<{
        addressId: number,
        claimerIds: Array<number>,
        dependentIndex: number,
        dependentFullName: string,
        dependentPersonId: number,
        isAbleToBeDependent: boolean,
        isChild: boolean,
        isChildClaimedBySeparatelyLivingParent: boolean,
        isDependent: boolean,
        isClaimedByITF: boolean,
        parents: Array<number>,
        siblings: Array<number>,
        spouse: Array<number>
    }>
}

export interface Relations {
    loadedRelations: Array<Relation>,
    loadedRelationsWholeList: Array<GenericRelation>
}

export interface HouseholdMemberStatusSummary {
    personName: string,
    personId: number,
    personGuid: number,
    personIndex: number,
    addressId: number,
    relationToCurrentHhm: string,
    relationCode: string,
    applyingForCoverage: boolean | null,
    dateOfBirth: string | null
}

interface SsapReduxForm {
    [ key: string ]: {
        isFormSubmitted: boolean
    }
}

interface taxHousehold {
    bloodRelationship: BloodRelationships,
    householdMember: HouseholdMembers,
    medicaidConsent: boolean | null,
    otherAddresses: Array<Address>,
    primaryTaxFilerPersonId: null | number,
}

export enum VoterRegistration {
    APPLYING_FOR_REGISTRATION = 'APPLYING_FOR_REGISTRATION',
    NOT_APPLYING_FOR_REGISTRATION = 'NOT_APPLYING_FOR_REGISTRATION',
    ALREADY_REGISTERED = 'ALREADY_REGISTERED',
    UNSPECIFIED = 'UNSPECIFIED'
}

export interface SsapData {
    agreeToEndCoverage: boolean | null,
    agreeToUseIncomeData: boolean | null,
    applyingForFinancialAssistanceIndicator: boolean | null,
    ssapApplicationId: string,
    claimNoticeDMAHSAcknowledgment: boolean | null,
    consentAgreement: boolean | null,
    coverageYear: number,
    incarcerationAsAttestedIndicator: boolean | null,
    legalAttestations: {
        absentParentAgreementIndicator: boolean | null,
        agreeToMoneyPursual: boolean | null,
        aptcAgreementIndicator: boolean | null,
        changeInformationAgreementIndicator: boolean | null,
        isTruthful: boolean | null,
        medicaidContactAgreementIndicator: boolean | null,
        medicaidRequirementAgreementIndicator: boolean | null,
        penaltyOfPerjuryAgreementIndicator: boolean | null,
        turquoiseConsent: boolean | null,
        voterRegistration: VoterRegistration
    },
    maxAchievedPage: number,
    numberOfYearsAgreed: string | null,
    removedMembers: {
        [ key: string ]: RemovedMember
    },
    taxHousehold: taxHousehold
}

export interface ModalHeaderProps {
    closeAllModals?: boolean,
    hideCloseIcon?: boolean,
    hideModal: Function,
    hideAllModals?: Function,
    hideModalCallback?: Function,
    modalId: string,
    modalHeaderTextData: string,
    primaryTopAnchor: { }
}

export interface CommonModalProps {
    actions: {
        [ key: string ]: Function
    },
    bottomAnchors: Array<any>,
    boundAnchor: Function,
    currentHhm: HouseholdMember,
    formName?: string,
    hhmIndex: number,
    householdMembers: HouseholdMembers,
    methods?: { },
    modalHeaderProps: ModalHeaderProps
}

export type OnChangeArgTypes = [
    SsapData & HouseholdMember & HraDetails, // needs to be extended
    any,
    string[],
    [
        string,
        any,
        boolean?,
        string?
    ]
]

export interface SsapSavePayload {
    application: string,
    applicationId: string,
    csrOverride: boolean,
    recalculateIncome: boolean,
    ssnToUpdate: {} | null,
    immigrationInfoToUpdate: {} | null
}

interface SsapFlow {
    isFlowLoaded: boolean,
    flowType: SsapFlowType
}

interface SsapStore {
    adjustedFlow: Array<any>
    counties: {
        [ key: string ]: Array<{
            [ key: number ]: string
        }>
    },
    data: SsapData,
    dataSnapshot: SsapData,
    enrollments: Array<Enrollment>,
    flow: SsapFlow,
    form: SsapReduxForm,
    hyperReview: boolean,
    methods: {
        [ key: string ]: {
            [ key: string ]: Function
        }
    },
    page: number,
    scroll: string | null,
    relations: Relations,
    structure: {
        bloodRelationshipStructure: string,
        taxHouseholdStructure: string
    }
}


export interface CommonStore {
    environment: {
        exchangeName: string,
        impersonation: {} | null,
        isState: IsState,
        links: {
            PHONE: string,
            REGISTER_FOR_VOTE: string
        },
        localDates: {
            serverDate: Dayjs
        },
        stateCode: IEX_STATE_CODES
    }
}

export interface SsapReduxActions {
    changeData: Function,
    clearMethods: Function,
    clearScrollToElement: Function,
    fetchAllBloodRelationships: Function,
    fetchCountiesForZipcode: Function,
    fetchEnrollments: Function,
    removeCachedData: Function,
    updateCachedSSN: Function,
    scrollToElement: Function,
    setCounty: Function,
    setMethods: Function,
    showModal: Function,
    submitApplication: Function
}

export interface ConnectedSsapStoreActions {
    actions: SsapReduxActions
}

export interface ReduxAction {
    type: ReduxActionType,
    preferences?: CommunicationPreferences
}

interface ConnectedSsapStoreAndActions extends CommonStore,  ConnectedSsapStoreActions, SsapStore {
    householdMembers: HouseholdMembers,
    relations: Relations,
    taxHousehold: taxHousehold
}

interface ButtonContainer { // TODO: specify ButtonContainer props
    args: Array<any>,
    customButtonClassName?: string,
    type: string,
    ariaLabel?: string
}

interface PagesContainer {
    addressDefaults: {
        defaultMailingAddressId: number
    },
    buttonContainer: FunctionComponent<ButtonContainer>,
    currentHhm: HouseholdMember,
    familyStatus: {
        dependentsOfCaretaker: Array<HouseholdMemberStatusSummary>
    },
    formName: string,
    history: History,
    hhmIndex: number,
    itfFeatureEnabled: boolean,
    memberRemovalReasonEnabled: boolean,
    pageName: SsapPageName
}

export interface SsapPage extends ConnectedSsapStoreAndActions, PagesContainer {}

export interface ApplicationStore {
    common: CommonStore,
    ssap: SsapStore
}

export type IExchangeProgramNames = {
    exchangeName: string;
    exchangeMedicaid: string;
    exchangeChip: string;
    exchangeMedicaidOrChip: string;
    exchangeMedicaidSlashChip: string;
};